import * as React from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";
import SEO from "../components/common/SEO";
import "./AboutPage.css";

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="About"
        description="Learn about Loops & Purls — a modern web app that brings traditional knitting pattern design into the digital age. Create stitch-by-stitch charts, export PDFs, and share patterns — no account required."
        keywords="about Loops & Purls, knitting pattern app, pattern design tool, knitting software, digital knitting charts, pattern maker history"
      />
      <Box component="main" className="fade-in-up">
      <Container maxWidth="lg" className="about-container">
        {/* Header */}
        <Box className="center mb-4 mt-4">
          <Typography variant="h2" color="primary">
            About Loops & Purls
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            className="mt-1"
          >
            A modern web application designed to help knitters create and share
            custom patterns with ease — no account required.
          </Typography>
          {/* Accent divider (CSS: .hr-accent in plain theme) */}
          <Box component="hr" className="hr-accent" aria-hidden="true" />
        </Box>

        {/* Marketing Hero Blurb (glass paper variant feel) */}
        <Paper
          variant="glass"
          elevation={0}
          className="paper paper--glass mb-4"
        >
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
            <Typography variant="body1" className="flex-grow-1">
              <strong>Born from a knitter's frustration.</strong> Loops & Purls was created by a lifelong knitter who kept finding beautiful lace patterns — but only in chart form. As someone who preferred written instructions, the solution was clear: build a tool that converts charts into the written patterns knitters need. Today, it serves both chart-lovers and written-pattern enthusiasts, helping knitters of all levels create, learn, and share.
            </Typography>
            <Button
              onClick={() => navigate("/chart-maker")}
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardRoundedIcon />}
              className="flex-shrink-0"
            >
              Try Chart Maker
            </Button>
          </div>
        </Paper>

        {/* All Three Sections in One Row - Flexbox Approach */}
        <div className="about-cards">
          {/* Left: What is Loops & Purls */}
          <div className="about-card">
            <Paper
              variant="woven"
              elevation={1}
              className="paper paper--woven flex-fill d-flex flex-column"
            >
              <Typography variant="h5" color="primary" className="mb-3">
                What is Loops & Purls?
              </Typography>

              <Typography variant="body1" className="mb-2">
                Loops & Purls is a modern web app that brings traditional
                pattern design into the digital age. Built by someone who's been knitting since childhood and has spent years teaching others — from founding a middle school knitting club to leading community classes — it's designed with real knitters in mind.
              </Typography>

              <Typography variant="body1" className="mb-2">
                The creator started as a written-pattern knitter who struggled with chart-only lace patterns. After building this tool to convert charts into written instructions, they discovered a new appreciation for charts and how they work. Today, Loops & Purls serves both preferences, helping knitters bridge the gap between visual charts and written patterns.
              </Typography>

              <Typography variant="body1">
                Whether you're sketching your first scarf or mapping a complex
                lace chart, Loops & Purls helps you visualize stitches, learn new techniques through comprehensive stitch guides, and export your patterns beautifully — no graph paper required. It's built for knitters and many other fiber arts enthusiasts who want clarity, flexibility, and professional results.
              </Typography>
            </Paper>
          </div>

          {/* Center: Key Features */}
          <div className="about-card">
            <Paper
              variant="woven"
              elevation={1}
              className="paper paper--woven flex-fill d-flex flex-column"
            >
              <Typography variant="h5" color="primary" className="mb-3">
                Key Features
              </Typography>

              <List dense>
                {[
                  "Chart to Written Conversion — automatically generate row-by-row instructions from any chart",
                  "Comprehensive Stitch Guides — learn symbols and techniques with built-in reference",
                  "Dynamic Grid System — stitch-by-stitch up to 50×50",
                  "Export Options — PDF with charts and instructions, or JSON for editing",
                  "Community Gallery — share your patterns and discover others' work",
                ].map((text, i) => {
                  const [title, subtitle] = text.split(" — ");
                  return (
                    <ListItem key={i}>
                      <ListItemIcon>
                        <CheckCircleRoundedIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight={800}>
                            {title}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {subtitle}
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </div>

          {/* Right: Getting Started */}
          <div className="about-card">
            <Paper
              variant="woven"
              elevation={1}
              className="paper paper--woven flex-fill d-flex flex-column"
            >
              <Typography variant="h5" color="primary" className="mb-3">
                Getting Started
              </Typography>

              <Typography variant="body1" className="mb-3">
                <strong>No account required!</strong> You can start creating
                patterns immediately.
              </Typography>

              <ol className="mb-3">
                <Typography component="li" variant="body1">
                  Go to <strong>Chart Maker</strong> and set your grid size
                </Typography>
                <Typography component="li" variant="body1">
                  Fill in your pattern using the stitch palette and symbol guide
                </Typography>
                <Typography component="li" variant="body1">
                  Watch written instructions generate automatically as you work
                </Typography>
                <Typography component="li" variant="body1">
                  Export PDF or JSON to save, print, or share your pattern
                </Typography>
              </ol>

              <Typography
                variant="body2"
                color="text.secondary"
                className="mb-3"
              >
                Works beautifully on desktop and tablet — and is fully
                responsive for mobile use.
              </Typography>

              <div className="d-flex flex-column gap-2">
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => navigate("/chart-maker")}
                >
                  Try Chart Maker
                </Button>
              </div>
            </Paper>
          </div>
        </div>

        {/* Join the Community Section */}
        <Paper
          variant="woven"
          elevation={1}
          className="paper paper--woven mt-4 mb-4"
        >
          <Box className="p-4 text-center">
            <Typography variant="h5" color="primary" className="mb-2">
              Join the Community
            </Typography>
            <Typography variant="body1" className="mb-3">
              Share your patterns in our gallery and discover beautiful designs from other knitters. Browse by tags, search for inspiration, and export any pattern you find. Every pattern in the gallery is free to use and share.
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => navigate("/gallery")}
            >
              Browse Gallery
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
    </>
  );
}
